import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { TreeNode } from 'primeng/api';

import { TreeNodeData } from '../model/tree-node-data';

@Component( {
    selector: 'his-tree-select-popup',
    templateUrl: './his-tree-select-popup.component.html',
    styleUrls: ['./his-tree-select-popup.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisTreeSelectPopupComponent implements AfterViewInit {

    /*
     * If an item was selected it's selected attribute will be set to 'true'.
     * An item that was selected with subtree elements will have attribute
     * subTreeSelected = 'true' and selected = 'false'.
     * Both will be set into the selectedData array, and are distinguishable due
     * to either having selected being 'true' or subTreeSelected being 'true'.
     * Any item not in selectedData is neither selected nor subTreeSelected.
     */

    @Input() title: string;
    @Input() treename: string;

    // select only a single element?
    @Input() singleSelect = false;

    // display the delete selection button?
    @Input() showDeleteButton = true;
    // display column with subtree select checkboxes?
    @Input() showSubtreeSelectColumn = true;

    // The select tree. The TreeTable component requires an _array_ of TreeNode objects as its value.
    @Input() tree: TreeNode[];
	
	@Input() noSelectableNodeIds: number[] = [];
	
	@Input() noSelectableSubtreeNodeIds: number[] = [];

    // selectedData contains TreeNode.data objects for all tree nodes that are selected
    // (either individually or with it's complete subtree). Entries need an id attribute
    // and either a selected="true" or a "subTreeSelected" attribute.
    @Input() selectedData: TreeNodeData[] = [];

    @Input() isFirstInputElement = false;
    @ViewChild('inputElement') inputElement: ElementRef;

    visible = false;

    @Output() selectedDataChange = new EventEmitter<any[]>();

    ngAfterViewInit() {
        // Set focus on first input element
        if (this.isFirstInputElement) {
            setTimeout( () => this.inputElement.nativeElement.focus() );
        }
    }

    /**
     * shows the popup window
     */
    public show(): void {
        this.updateTreeSelection( this.tree );
        this.visible = true;
    }

    /**
     * Set the selected and subTreeSelected attributes of tree nodes from selectedData,
     * and expand the paths to all selected nodes.
     *
     * @param roots roots of the trees
     */
    private updateTreeSelection( roots: TreeNode[] ): void {
        // collect ids of selected nodes from selectedData.
        // subtree selection has priority before single element selection
        let selectedIdSet = new Set<number>();
        let subTreeSelectedIdSet = new Set<number>();
        for ( let selectedElement of this.selectedData ) {
            if ( selectedElement.subTreeSelected ) {
                subTreeSelectedIdSet.add( selectedElement.id );
                console.log('oererer: ', subTreeSelectedIdSet);
            } else if ( selectedElement.selected ) {
                selectedIdSet.add( selectedElement.id );
            }
        }
        console.log( 'HisTreeSelectPopupComponent.updateTreeSelection(): selectedIdSet =', selectedIdSet );
        console.log( 'HisTreeSelectPopupComponent.updateTreeSelection(): subTreeSelectedIdSet =', subTreeSelectedIdSet );

        // Recurrence
        this.updateSubtreeSelection( roots, selectedIdSet, subTreeSelectedIdSet, [] );

        // Since Angular 7 we need to change the root objects to trigger a complete re-rendering of the tree.
        // https://github.com/primefaces/primeng/issues/261
        // https://github.com/primefaces/primeng/issues/5872
        this.tree = [... this.tree];
    }

    private updateSubtreeSelection(
        treeNodes: TreeNode[], selectedIdSet: Set<number>,
        subTreeSelectedIdSet: Set<number>, path: TreeNode[] ) {

        for ( let treeNode of treeNodes ) {
            // set selection attributes
            treeNode.data.selected = selectedIdSet.has( treeNode.data.id );
            // console.log("treeNode id =" , treeNode.data.id , ", selected =" , treeNode.data.selected);
            treeNode.data.subTreeSelected = subTreeSelectedIdSet.has( treeNode.data.id );
            // Expand paths to selected nodes.
            // Usually we would simply want to evaluate TreeNode.parent.
            // However there is a PrimeNG bug in the way: parent-information is not filled before a node has been expanded.
            // See see https://forum.primefaces.org/viewtopic.php?f=35&t=49704
            // This is the reason why we have to pass the node path as a method argument from above
            if ( treeNode.data.selected || treeNode.data.subTreeSelected ) {
                for ( let ancestor of path ) {
                    ancestor.expanded = true;
                }
            }
            // recurrence
            if ( treeNode.children && treeNode.children.length > 0 ) {
                let extendedPath = [...path, treeNode]; // new array object required
                this.updateSubtreeSelection( treeNode.children, selectedIdSet, subTreeSelectedIdSet, extendedPath );
            }
        }
    }

    /**
     * closes the popup and updates the selection
     *
     * @param e event
     */
    public save( e: Event ): void {
        this.selectedData = [];
        this.saveTreeSelection( this.tree );
        console.log( 'HisTreeSelectPopupComponent: Emit save event:', this.selectedData );
        this.selectedDataChange.emit( this.selectedData );
        this.visible = false;
        e.preventDefault();
    }

    /**
     * Saves the selection from the tree to selectedData.
     *
     * @param treeNodes roots of the trees
     */
    private saveTreeSelection( treeNodes: TreeNode[] ): void {
        for ( let treeNode of treeNodes ) {
            if ( treeNode.data.subTreeSelected ) {
                treeNode.data.selected = false;
            }
            if ( treeNode.data.selected || treeNode.data.subTreeSelected ) {
                // Copy data to stop data binding from treeNode to selectedData.
                // A deep copy is not required because all tree nodes have an id attribute.
                // We need Object.assign() because a treeNode.data object can have more
                // properties than id, selected and subTreeSelected...
                let dataCopy: TreeNodeData = { id: treeNode.data.id };
                Object.assign( dataCopy, treeNode.data );
                this.selectedData.push( dataCopy );
            }
            // Recurrence, but ignore the children of nodes having subTreeSelected="true"
            if ( treeNode.children && !treeNode.data.subTreeSelected ) {
                this.saveTreeSelection( treeNode.children );
            }
        }
    }

    /**
     * closes the popup without changing the selection
     *
     * @param e event
     */
    public cancel( e: Event ): void {
        this.visible = false;
        e.preventDefault();
    }

    /**
     * Select a single node from the tree.
     *
     * @param selection the selected tree node
     */
    doSingleSelect( selection: TreeNode ) {
        console.log( 'Submit single-selection:', selection );
        this.selectedData = [selection.data];
        this.selectedDataChange.emit( this.selectedData );
        this.visible = false;
    }

    public deleteAll() {
        this.selectedData = [];
        console.log( 'HisTreeSelectPopupComponent: Emit deleteAll event:', this.selectedData );
        this.selectedDataChange.emit( this.selectedData );
    }
	
    isNodeSelectable(rowData: TreeNodeData): boolean {
        return this.noSelectableNodeIds.includes(rowData.id);
    }
	
    isSubtreeNodeSelectable(rowData: TreeNodeData): boolean {
	    return this.noSelectableSubtreeNodeIds.includes(rowData.id);
    }
}
